import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft, Check, Save } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import { useCreateAgent } from "@/features/agents/hooks";
import { useProviderOptions } from "@/features/assistants/hooks";
import type { AgentTemplate } from "@/features/agents/api";
import LiveVoicePicker from "@/features/agents/components/LiveVoicePicker";
import TemplatePickerCards from "@/features/agents/components/TemplatePickerCards";
import CreateTemplateDialog from "@/features/agents/components/CreateTemplateDialog";

const STEPS = ["Template", "Basic Info", "Voice", "Prompt", "Review"] as const;

type Form = {
  name: string;
  description: string;
  agent_type: string;
  language: string;
  voice_provider: string;
  voice_id: string;
  voice_settings: { voice_name?: string };
  llm_provider: string;
  llm_model: string;
  system_prompt: string;
  initial_message: string;
  template: string | null;
};

const INITIAL: Form = {
  name: "",
  description: "",
  agent_type: "single_prompt",
  language: "en-US",
  voice_provider: "elevenlabs",
  voice_id: "21m00Tcm4TlvDq8ikWAM",
  voice_settings: { voice_name: "Rachel" },
  llm_provider: "openai",
  llm_model: "gpt-5.6-luna",
  system_prompt: "",
  initial_message: "",
  template: null,
};

export default function AgentCreatePage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<Form>(INITIAL);
  const [error, setError] = useState("");
  const create = useCreateAgent();
  // New agents start on the catalog's default model (Admin → Provider options).
  const defaultModel = useProviderOptions("model").options.find((o) => o.is_default && o.api_id);

  function update<K extends keyof Form>(key: K, value: Form[K]) {
    setForm((p) => ({ ...p, [key]: value }));
  }

  async function submit() {
    setError("");
    try {
      await create.mutateAsync(
        defaultModel ? { ...form, llm_provider: defaultModel.provider, llm_model: defaultModel.api_id ?? form.llm_model } : form,
      );
      toast.success("Agent created");
      navigate("/agents");
    } catch (e) {
      const err = e as {
        response?: { data?: { error?: { message?: string } } };
      };
      setError(
        err?.response?.data?.error?.message || "Failed to create agent.",
      );
    }
  }

  function applyTemplate(tpl: AgentTemplate | null) {
    if (!tpl) {
      // "Start blank" — keep current form, just advance.
      setForm((p) => ({ ...p, template: null }));
      setStep(1);
      return;
    }
    setForm((p) => ({
      ...p,
      description: tpl.description || p.description,
      language: tpl.default_language || p.language,
      voice_provider: tpl.default_voice_provider || p.voice_provider,
      voice_id: tpl.default_voice_id || p.voice_id,
      // Template IDs come without a display name — don't keep a stale one.
      voice_settings: tpl.default_voice_id ? { voice_name: "" } : p.voice_settings,
      system_prompt: tpl.default_system_prompt,
      initial_message: tpl.default_initial_message,
      template: tpl.id,
    }));
    setStep(1);
  }

  const canNext =
    // Step 0 (Template) advances via card click, not Next button.
    (step === 1 && form.name.trim().length > 0) ||
    (step === 2 && form.voice_id.trim().length > 0) ||
    (step === 3 && form.system_prompt.trim().length > 0) ||
    step === 4;

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/agents">
            <ArrowLeft className="size-4" />
            Back
          </Link>
        </Button>
        <h1 className="text-2xl font-semibold tracking-tight">Create Agent</h1>
      </div>

      <Stepper current={step} />

      {error && (
        <div className="rounded-md border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
          {error}
        </div>
      )}

      <Card>
        <CardContent className="space-y-5 p-6">
          {step === 0 && (
            <TemplateStep
              onPick={applyTemplate}
              selectedId={form.template}
            />
          )}
          {step === 1 && (
            <BasicInfoStep form={form} update={update} />
          )}
          {step === 2 && (
            <VoiceStep form={form} update={update} />
          )}
          {step === 3 && <PromptStep form={form} update={update} />}
          {step === 4 && <ReviewStep form={form} />}
        </CardContent>
      </Card>

      <div className="flex justify-end gap-2">
        {step > 0 && (
          <Button variant="outline" onClick={() => setStep(step - 1)}>
            Back
          </Button>
        )}
        {step === 0 ? null : step < STEPS.length - 1 ? (
          <Button onClick={() => setStep(step + 1)} disabled={!canNext}>
            Next
          </Button>
        ) : (
          <Button onClick={submit} disabled={create.isPending}>
            <Save className="size-4" />
            {create.isPending ? "Creating…" : "Create Agent"}
          </Button>
        )}
      </div>
    </div>
  );
}

function Stepper({ current }: { current: number }) {
  return (
    <ol className="flex items-center gap-2 text-xs">
      {STEPS.map((label, i) => (
        <li key={label} className="flex flex-1 items-center gap-2">
          <span
            className={cn(
              "flex size-6 shrink-0 items-center justify-center rounded-full border text-xs font-semibold",
              i < current && "border-primary bg-primary text-primary-foreground",
              i === current && "border-primary text-primary",
              i > current && "border-input text-muted-foreground",
            )}
          >
            {i < current ? <Check className="size-3.5" /> : i + 1}
          </span>
          <span
            className={cn(
              "text-sm font-medium",
              i === current ? "text-foreground" : "text-muted-foreground",
            )}
          >
            {label}
          </span>
          {i < STEPS.length - 1 && (
            <span
              className={cn(
                "ml-1 h-px flex-1",
                i < current ? "bg-primary" : "bg-input",
              )}
            />
          )}
        </li>
      ))}
    </ol>
  );
}

function TemplateStep({
  onPick,
  selectedId,
}: {
  onPick: (tpl: AgentTemplate | null) => void;
  selectedId: string | null;
}) {
  const [createOpen, setCreateOpen] = useState(false);
  return (
    <div className="space-y-5">
      <div>
        <h2 className="text-lg font-semibold">Start from a template</h2>
        <p className="text-sm text-muted-foreground">
          Pick a vertical to pre-fill the agent's prompt and voice settings.
          You can edit anything afterwards.
        </p>
      </div>
      <TemplatePickerCards
        onPick={onPick}
        selectedId={selectedId}
        onCreateNew={() => setCreateOpen(true)}
      />
      <CreateTemplateDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={(tpl) => onPick(tpl)}
      />
    </div>
  );
}

function BasicInfoStep({
  form,
  update,
}: {
  form: Form;
  update: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="name">Agent Name</Label>
        <Input
          id="name"
          value={form.name}
          onChange={(e) => update("name", e.target.value)}
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="description">Description</Label>
        <Textarea
          id="description"
          rows={3}
          value={form.description}
          onChange={(e) => update("description", e.target.value)}
        />
      </div>
      <div className="space-y-1.5">
        <Label>Agent Type</Label>
        <Select
          value={form.agent_type}
          onValueChange={(v) => update("agent_type", v)}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="single_prompt">Single Prompt</SelectItem>
            <SelectItem value="flow_based">Flow Based</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </>
  );
}

function VoiceStep({
  form,
  update,
}: {
  form: Form;
  update: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  return (
    <>
      {/* Pipeline version is set by the platform default (admin-configured in
          Platform Settings) at creation; it can be changed per agent later on
          the agent's Configuration tab. */}
      <div className="space-y-1.5">
        <Label>Voice Provider</Label>
        <Select
          value={form.voice_provider}
          onValueChange={(v) => {
            update("voice_provider", v);
            // A voice ID belongs to one provider — force a fresh pick.
            update("voice_id", "");
            update("voice_settings", { voice_name: "" });
          }}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
            <SelectItem value="cartesia">Cartesia</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {form.voice_provider === "elevenlabs" && (
        <ProviderNote tone="info">
          <strong>Multilingual:</strong> auto-detects the caller&apos;s language
          and replies in the same one. Supported: English, Spanish, French,
          German, Hindi, Arabic, Portuguese.
        </ProviderNote>
      )}
      {form.voice_provider === "cartesia" && (
        <ProviderNote tone="warning">
          <strong>English only.</strong> Cartesia voices currently support
          English. For multilingual support, switch to ElevenLabs.
        </ProviderNote>
      )}

      <div className="space-y-1.5">
        <Label>Voice</Label>
        <LiveVoicePicker
          provider={form.voice_provider}
          value={form.voice_id}
          valueName={form.voice_settings.voice_name}
          onSelect={(v) => {
            update("voice_id", v.voice_id);
            update("voice_settings", { voice_name: v.name });
          }}
        />
      </div>
    </>
  );
}

function ProviderNote({
  tone,
  children,
}: {
  tone: "info" | "warning";
  children: React.ReactNode;
}) {
  return (
    <p
      className={cn(
        "rounded-md border px-3 py-2 text-sm",
        tone === "info" &&
          "border-blue-200 bg-blue-50 text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200",
        tone === "warning" &&
          "border-amber-200 bg-amber-50 text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200",
      )}
    >
      {children}
    </p>
  );
}

function PromptStep({
  form,
  update,
}: {
  form: Form;
  update: <K extends keyof Form>(k: K, v: Form[K]) => void;
}) {
  return (
    <>
      <div className="space-y-1.5">
        <Label htmlFor="prompt">System Prompt</Label>
        <Textarea
          id="prompt"
          rows={10}
          value={form.system_prompt}
          onChange={(e) => update("system_prompt", e.target.value)}
          placeholder="Define how your agent should behave…"
          required
        />
      </div>
      <div className="space-y-1.5">
        <Label htmlFor="initial">Initial Message (optional)</Label>
        <Textarea
          id="initial"
          rows={3}
          value={form.initial_message}
          onChange={(e) => update("initial_message", e.target.value)}
          placeholder="Message spoken when the call starts…"
        />
      </div>
    </>
  );
}

function ReviewStep({ form }: { form: Form }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Review</CardTitle>
        <CardDescription>
          Confirm the settings before creating.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Row label="Name" value={form.name} />
          <Row label="Type" value={form.agent_type} />
          <Row label="Voice provider" value={form.voice_provider} />
          <Row label="Voice" value={form.voice_settings.voice_name || form.voice_id} />
          <Row
            label="Languages"
            value={
              form.voice_provider === "elevenlabs"
                ? "Multilingual (auto-detect)"
                : "English only"
            }
          />
        </dl>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="break-words text-sm">{value || "—"}</dd>
    </div>
  );
}
